import { useEffect, useState, useRef, useCallback } from "react";
import { createConsumer } from "@rails/actioncable";

const useActionCable = (channel, params = {}, callbacks = {}) => {
  const {
    onConnected,
    onDisconnected,
    onReceived,
    onRejected,
    autoDisconnect = false,
    autoDisconnectCondition = () => false,
  } = callbacks;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  const cableRef = useRef(null);
  const subscriptionRef = useRef(null);

  const disconnect = useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
    if (cableRef.current) {
      cableRef.current.disconnect();
      cableRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const perform = useCallback((action, data = {}) => {
    if (subscriptionRef.current) {
      subscriptionRef.current.perform(action, data);
    }
  }, []);

  const send = useCallback((data) => {
    if (subscriptionRef.current) {
      subscriptionRef.current.send(data);
    }
  }, []);

  useEffect(() => {
    if (!channel) return;

    cableRef.current = createConsumer();

    subscriptionRef.current = cableRef.current.subscriptions.create(
      { channel, ...params },
      {
        connected() {
          setIsConnected(true);
          onConnected?.();
        },
        disconnected() {
          setIsConnected(false);
          onDisconnected?.();
        },
        rejected() {
          onRejected?.();
        },
        received(data) {
          setLastMessage(data);
          onReceived?.(data);

          if (autoDisconnect && autoDisconnectCondition(data)) {
            disconnect();
          }
        },
      }
    );

    return () => {
      disconnect();
    };
  }, [channel, JSON.stringify(params)]);

  return {
    isConnected,
    lastMessage,
    disconnect,
    perform,
    send,
  };
};

export default useActionCable;
